import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { IconInfoCircle, IconPlus, IconX, IconFile } from '@tabler/icons-react';
import { Button } from '@/components/Button';
import { api } from '@/api/endpoints';
import { useUI } from '@/store/ui';

const MAX_TEXT = 5000;
const MAX_FILES = 10;
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_PREFIX = ['image/', 'video/'];
const ALLOWED_EXACT = ['application/pdf'];

function isAllowed(mime: string): boolean {
  return ALLOWED_EXACT.includes(mime) || ALLOWED_PREFIX.some(p => mime.startsWith(p));
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SupportModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const showToast = useUI(s => s.showToast);

  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => { setText(''); setFiles([]); setShowInfo(false); };
  const close = () => { reset(); onClose(); };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (picked.length === 0) return;

    const next = [...files];
    for (const f of picked) {
      if (next.length >= MAX_FILES) { showToast(t('profile.support_too_many_files'), 'error'); break; }
      if (f.size > MAX_BYTES)        { showToast(t('profile.support_file_too_big'),   'error'); continue; }
      if (!isAllowed(f.type))        { showToast(t('profile.support_file_bad_type'),  'error'); continue; }
      next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (idx: number) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true);
    try {
      await api.support.submit(msg, files);
      showToast(t('profile.support_sent'), 'success');
      close();
    } catch {
      showToast(t('errors.generic'), 'error');
    } finally {
      setSending(false);
    }
  };

  const canSend = text.trim().length >= 5 && text.length <= MAX_TEXT && !sending;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
          />
          <motion.div
            className="relative w-full max-w-[min(420px,100%)] bg-bg rounded-xl shadow-lg flex flex-col max-h-[85vh]"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          >
            <header className="px-4 py-3 border-b border-text/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-base font-medium">{t('profile.support_title')}</span>
                <button
                  type="button"
                  onClick={() => setShowInfo(v => !v)}
                  className="text-text-muted hover:text-text"
                  aria-label="info"
                >
                  <IconInfoCircle size={16} />
                </button>
              </div>
              <button
                onClick={close}
                className="w-8 h-8 grid place-items-center rounded-pill hover:bg-surface text-text-muted"
                aria-label="close"
              >
                <IconX size={18} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
              {showInfo && (
                <div className="text-xs text-text-muted bg-surface rounded-md p-3">
                  {t('profile.support_info')}
                </div>
              )}

              <div>
                <label className="block text-sm mb-1">{t('profile.support_message_label')}</label>
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value.slice(0, MAX_TEXT))}
                  placeholder={t('profile.support_placeholder')}
                  className="w-full h-32 p-3 rounded-lg bg-surface text-sm outline-none resize-none border border-accent/30 focus:border-accent"
                />
                <div className="mt-1 text-xs text-text-muted">{text.length}/{MAX_TEXT}</div>
              </div>

              <div>
                <label className="block text-sm mb-2">{t('profile.support_files_label')}</label>
                <div className="grid grid-cols-4 gap-2">
                  {files.map((f, idx) => (
                    <FilePreview key={idx} file={f} onRemove={() => removeFile(idx)} />
                  ))}
                  {files.length < MAX_FILES && (
                    <button
                      type="button"
                      onClick={() => inputRef.current?.click()}
                      className="aspect-square rounded-lg border-2 border-dashed border-accent/40 grid place-items-center text-accent hover:bg-surface"
                      aria-label="add"
                    >
                      <IconPlus size={28} />
                    </button>
                  )}
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept="image/*,application/pdf,video/*"
                  className="hidden"
                  onChange={onPick}
                />
              </div>
            </div>

            <div className="px-4 py-3 border-t border-text/10 space-y-2">
              <Button block onClick={submit} disabled={!canSend}>
                {sending ? t('profile.support_sending') : t('profile.support_send')}
              </Button>
              <Button block variant="secondary" onClick={close} disabled={sending}>
                {t('profile.support_cancel')}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function FilePreview({ file, onRemove }: { file: File; onRemove: () => void }) {
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  const objectUrl = (isImage || isVideo) ? URL.createObjectURL(file) : null;

  return (
    <div className="relative aspect-square rounded-lg bg-surface overflow-hidden">
      {isImage && objectUrl && <img src={objectUrl} alt="" className="w-full h-full object-cover" />}
      {isVideo && objectUrl && <video src={objectUrl} className="w-full h-full object-cover" muted />}
      {!isImage && !isVideo && (
        <div className="w-full h-full grid place-items-center text-text-muted">
          <IconFile size={28} />
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-bg/80 grid place-items-center hover:bg-bg"
        aria-label="remove"
      >
        <IconX size={12} />
      </button>
    </div>
  );
}
